/**
 * <TutorLiveActivityTabs> — l'encart de l'activité ouverte au centre du live du masteur : son titre, ses vues, le
 * côte à côte et la croix qui la ferme (molecule).
 *
 * Live du 04/11 : les onglets du centre se rangent en deux familles, celle de l'élève et celle de l'activité. Ce qui
 * est à l'activité se lit ensemble, dans une carte encastrée, et part avec elle. **Le titre cède sa place aux vues** :
 * il ne se montre que quand l'encart a la place des deux (`TITLE_FROM`, sa propre largeur) ; plus étroit, le livre
 * seul, et l'onglet ouvert ne sort jamais de la rangée. Le lecteur d'écran garde le titre dans le nom de la rangée.
 *
 * Test du 04/11 après-midi (Wajih) : la lavande de l'encart se distinguait trop peu de la page, où se posent les
 * onglets de l'élève. L'encart est une carte blanche, son ombre, et le liseré de l'univers : la façon du design system
 * de distinguer une carte sans en faire LA carte (`leftBorderColor`).
 *
 * Contrôlée : la vue ouverte, le côte à côte et la fermeture arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Divider } from "../../atoms/Divider"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { Card } from "../Card"
import { type TabItem, Tabs } from "../Tabs"
import { LIVE_CHOSEN_FACE } from "../TutorLiveControlBar"

export type TutorLiveActivityTabsProps<K extends string> = {
    /** Le titre de l'activité ouverte. */
    title: string
    items: TabItem<K>[]
    /** La vue ouverte ; une valeur hors de ces vues (un onglet de l'élève) ne souligne rien. */
    value: K
    onChange: (key: K) => void
    /** « Côte à côte », au bout de la rangée, quand le centre en a la place. Absent : pas de bascule. */
    split?: { on: boolean; onToggle: () => void }
    /** Fermer l'activité. Absent : pas de croix. */
    onClose?: () => void
    testID?: string
}

const TEXTS = {
    tabs: (title: string) => `Les vues de l'activité ${title}`,
    split: "Côte à côte",
    close: "Fermer l'activité",
}

/** Le titre et les vues tiennent ensemble à partir de cette largeur d'encart (1 440 px d'écran, le centre ouvert). */
const TITLE_FROM = 560
/** Le titre ne prend jamais la place des vues. */
const TITLE_MAX = 200

export function TutorLiveActivityTabs<K extends string>(props: TutorLiveActivityTabsProps<K>) {
    const { title, items, value, onChange, split, onClose, testID } = props
    const roomy = useWidthAtLeast(TITLE_FROM)
    return (
        <View style={styles.root} onLayout={roomy.onLayout} testID={testID}>
            <Card p="0" leftBorderColor="role" style={styles.card}>
                <Icon name="book-open" size={16} color="role" />
                {roomy.atLeast && (
                    <>
                        <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1} style={styles.title}>
                            {title}
                        </Text>
                        <Divider vertical spacing="1" />
                    </>
                )}
                <View style={styles.tabs}>
                    <Tabs
                        items={items}
                        value={value}
                        onChange={onChange}
                        size="sm"
                        fadeColor="surface-raised"
                        accessibilityLabel={TEXTS.tabs(title)}
                        trailing={
                            split != null ? (
                                <IconButton
                                    iconName="columns"
                                    shape="square"
                                    size={32}
                                    iconSize={18}
                                    {...(split.on
                                        ? LIVE_CHOSEN_FACE
                                        : { appearance: "transparent", iconColor: "text-strong" })}
                                    selected={split.on}
                                    onPress={split.onToggle}
                                    accessibilityLabel={TEXTS.split}
                                />
                            ) : undefined
                        }
                    />
                </View>
                {onClose != null && (
                    <IconButton
                        iconName="times"
                        shape="square"
                        size={28}
                        iconSize={16}
                        appearance="transparent"
                        iconColor="text-muted"
                        onPress={onClose}
                        accessibilityLabel={TEXTS.close}
                    />
                )}
            </Card>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minWidth: 0 },
    card: {
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        paddingLeft: theme.spacing["3"],
        paddingRight: theme.spacing["1"],
        borderRadius: theme.radius.md,
    },
    title: { flexShrink: 1, minWidth: 0, maxWidth: TITLE_MAX },
    tabs: { flex: 1, minWidth: 0 },
}))
